import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaCloud, FaChartLine, FaNetworkWired, FaCogs } from 'react-icons/fa';
import { ink, paper, paperMuted, borderInk, borderInkHover, bodyMuted, sky } from '../theme';
import BlueprintSectionTitle from './BlueprintSectionTitle';

const ProductsSection = styled.section`
  padding: 100px 0;
  background: ${paperMuted};
  position: relative;
  font-family: 'Inter', system-ui, sans-serif;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
`;

const SectionSubtitle = styled.p`
  font-size: 1.05rem;
  text-align: center;
  margin-bottom: 3rem;
  color: ${bodyMuted};
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 2.5rem;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    margin-bottom: 2rem;
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 25px;
  }

  @media (max-width: 480px) {
    gap: 20px;
  }
`;

const ProductCard = styled(motion.div)`
  background: ${paper};
  border: 1px solid ${borderInk};
  border-radius: 2px;
  padding: 0;
  overflow: hidden;
  transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
  text-align: left;
  position: relative;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  box-shadow: 0 4px 16px rgba(3, 26, 56, 0.05);

  &:hover {
    box-shadow: 0 10px 28px rgba(3, 26, 56, 0.08);
    transform: ${props => props.clickable ? 'translateY(-4px)' : 'none'};
    border-color: ${borderInkHover};
  }
`;

const ProductCardHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 18px 24px 20px calc(24px + 5px);
  position: relative;
  overflow: hidden;
  background: linear-gradient(168deg, #0e3a63 0%, ${ink} 52%, #021427 100%);
  border-bottom: 1px solid rgba(142, 207, 255, 0.22);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.07);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    opacity: 0.12;
    background-image:
      linear-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.4) 1px, transparent 1px);
    background-size: 12px 12px;
    pointer-events: none;
    filter: blur(0.2px);
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 5px;
    background: linear-gradient(180deg, ${sky} 0%, rgba(142, 207, 255, 0.35) 100%);
    box-shadow: 3px 0 12px rgba(142, 207, 255, 0.18);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    padding: 16px 20px 18px calc(20px + 5px);
  }

  @media (max-width: 480px) {
    padding: 14px 16px 16px calc(16px + 5px);
    gap: 12px;
  }
`;

const ProductHeadText = styled.div`
  position: relative;
  z-index: 1;
  flex: 1;
  min-width: 0;
`;

const ProductCardBody = styled.div`
  padding: 22px 28px 26px;

  @media (max-width: 768px) {
    padding: 20px 22px 24px;
  }

  @media (max-width: 480px) {
    padding: 18px 16px 22px;
  }
`;

const ProductIcon = styled.div`
  position: relative;
  z-index: 1;
  width: 52px;
  height: 52px;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(142, 207, 255, 0.45);
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  color: ${sky};
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
    font-size: 1.3rem;
  }

  @media (max-width: 480px) {
    width: 44px;
    height: 44px;
    font-size: 1.2rem;
  }
`;

const ProductTitle = styled.h3`
  position: relative;
  z-index: 1;
  font-size: 1.28rem;
  font-weight: 800;
  color: #f2f7ff;
  margin: 0 0 8px;
  letter-spacing: 0.02em;
  line-height: 1.2;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.45);

  @media (max-width: 768px) {
    font-size: 1.15rem;
  }

  @media (max-width: 480px) {
    font-size: 1.05rem;
    margin-bottom: 6px;
  }
`;

const ProductSubtitle = styled.p`
  position: relative;
  z-index: 1;
  color: rgba(255, 255, 255, 0.78);
  font-size: 0.86rem;
  font-weight: 500;
  margin: 0;
  line-height: 1.45;

  @media (max-width: 768px) {
    font-size: 0.84rem;
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const ProductInfo = styled.div`
  margin-bottom: 15px;
`;

const InfoLabel = styled.div`
  color: ${ink};
  font-size: 0.72rem;
  font-weight: 700;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.1em;

  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const InfoText = styled.p`
  color: ${bodyMuted};
  line-height: 1.65;
  font-size: 0.92rem;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    font-size: 0.88rem;
    line-height: 1.55;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
    line-height: 1.45;
  }
`;

const ProductsOverview = () => {
  const navigate = useNavigate();

  const products = [
    {
      icon: <FaCloud />,
      title: "e-node оазис (cloud)",
      subtitle: "Система безопасности для облачных сред",
      forWho: "DevOps-команды, архитекторы облачных инфраструктур",
      task: "Защита периметра и рабочих нагрузок в гибридных облаках",
      keyFunction: "Автоматическое обнаружение уязвимостей, мониторинг конфигураций безопасности и реагирование на инциденты в реальном времени",
      value: "Соответствие требованиям регуляторов без замедления разработки",
      clickable: true,
      onClick: () => navigate('/product/e-node-cloud')
    },
    {
      icon: <FaChartLine />,
      title: "E-node monitoring",
      subtitle: "Система мониторинга нового поколения",
      forWho: "Инженеры эксплуатации, NOC, технические директора",
      task: "Сквозная видимость состояния IT и OT-систем",
      keyFunction: "Сбор метрик с оборудования и ПО, предиктивная аналитика сбоев, умные алерты",
      value: "Предотвращение простоев за счет раннего обнаружения аномалий",
      clickable: false
    },
    {
      icon: <FaNetworkWired />,
      title: "E-node TM",
      subtitle: "Система управления надежностью и качеством информационного обмена в АСУ",
      forWho: "Предприятия с автоматизированными системами управления (АСУ ТП), энергетики, транспорт",
      task: "Гарантия целостности и достоверности телеметрии",
      keyFunction: "Валидация потоков данных, контроль задержек, аудит качества каналов связи",
      value: "Уверенность в том, что решения и управление оборудованием принимаются на основе точных данных",
      clickable: false
    },
    {
      icon: <FaCogs />,
      title: "E-node управление конфигурациями",
      subtitle: "Система управления конфигурациями оборудования",
      forWho: "Сетевые администраторы, инженеры по внедрению",
      task: "Централизованное управление настройками парка устройств",
      keyFunction: "Версионирование конфигов, массовое развертывание политик, откат изменений",
      value: "Исключение человеческого фактора при настройке критической инфраструктуры, контроль действий персонала",
      clickable: false
    }
  ];

  return (
    <ProductsSection id="products">
      <Container>
        <BlueprintSectionTitle $wide>
          Платформа E-node: технологический стек для цифрового контура
        </BlueprintSectionTitle>
        <SectionSubtitle>
          Линейка продуктов
        </SectionSubtitle>

        <ProductsGrid>
          {products.map((product, index) => (
            <ProductCard
              key={index}
              clickable={product.clickable}
              onClick={product.onClick}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <ProductCardHead>
                <ProductIcon>{product.icon}</ProductIcon>
                <ProductHeadText>
                  <ProductTitle>{product.title}</ProductTitle>
                  <ProductSubtitle>{product.subtitle}</ProductSubtitle>
                </ProductHeadText>
              </ProductCardHead>
              <ProductCardBody>
                <ProductInfo>
                  <InfoLabel>Для кого:</InfoLabel>
                  <InfoText>{product.forWho}</InfoText>
                </ProductInfo>

                <ProductInfo>
                  <InfoLabel>Задача:</InfoLabel>
                  <InfoText>{product.task}</InfoText>
                </ProductInfo>

                <ProductInfo>
                  <InfoLabel>Ключевая функция:</InfoLabel>
                  <InfoText>{product.keyFunction}</InfoText>
                </ProductInfo>

                <ProductInfo>
                  <InfoLabel>Ценность:</InfoLabel>
                  <InfoText>{product.value}</InfoText>
                </ProductInfo>
              </ProductCardBody>
            </ProductCard>
          ))}
        </ProductsGrid>
      </Container>
    </ProductsSection>
  );
};

export default ProductsOverview;
